import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "@/App";

/**
 * useLiveScores — Subscribes to /api/ws/live-scores (WebSocket) for real-time
 * updates every 20s. Falls back to HTTP polling every 60s if the WebSocket
 * cannot be established.
 *
 * Returns:
 *   { matches, sources, loading, source ('ws'|'poll'), connected, lastUpdate }
 */
export default function useLiveScores({ window: win = "today", sport = null } = {}) {
    const [matches, setMatches] = useState([]);
    const [sources, setSources] = useState([]);
    const [loading, setLoading] = useState(true);
    const [source, setSource] = useState("idle");
    const [connected, setConnected] = useState(false);
    const [lastUpdate, setLastUpdate] = useState(null);
    const wsRef = useRef(null);
    const pollTimer = useRef(null);
    const reconnectTimer = useRef(null);

    useEffect(() => {
        let isMounted = true;

        const apply = (data) => {
            if (!isMounted || !data) return;
            const items = data.matches || [];
            // If today returned nothing, the page-level component should request `recent` separately;
            // here we simply surface what came back.
            setMatches(items);
            setSources(data.sources || []);
            setLastUpdate(new Date());
            setLoading(false);
        };

        const startPolling = () => {
            setSource("poll");
            const fetchScores = async () => {
                try {
                    const params = new URLSearchParams({ window: win });
                    if (sport) params.append("sport", sport);
                    const { data } = await axios.get(`${API}/live-scores?${params.toString()}`);
                    let items = data?.matches || [];
                    // ESPN fallback — if nothing today, show most recent
                    if (items.length === 0 && win === "today") {
                        const { data: recent } = await axios.get(`${API}/live-scores?window=recent${sport ? `&sport=${sport}` : ""}`);
                        items = recent?.matches || [];
                        apply({ ...recent, matches: items });
                    } else {
                        apply(data);
                    }
                } catch (err) {
                    setLoading(false);
                }
            };
            fetchScores();
            pollTimer.current = setInterval(fetchScores, 60_000);
        };

        const connectWs = () => {
            try {
                const backend = process.env.REACT_APP_BACKEND_URL || window.location.origin;
                const wsUrl = backend.replace(/^http/, "ws") + `/api/ws/live-scores?window=${win}${sport ? `&sport=${sport}` : ""}`;
                const ws = new WebSocket(wsUrl);
                wsRef.current = ws;

                const fallbackTimer = setTimeout(() => {
                    // If WS doesn't open within 6s, fall back to polling
                    if (ws.readyState !== WebSocket.OPEN) {
                        try { ws.close(); } catch (_) {}
                        if (!pollTimer.current) startPolling();
                    }
                }, 6000);

                ws.onopen = () => {
                    clearTimeout(fallbackTimer);
                    setConnected(true);
                    setSource("ws");
                };
                ws.onmessage = (e) => {
                    try {
                        const msg = JSON.parse(e.data);
                        if (msg && msg.data) apply(msg.data);
                    } catch (_) { /* ignore */ }
                };
                ws.onerror = () => {
                    setConnected(false);
                };
                ws.onclose = () => {
                    setConnected(false);
                    clearTimeout(fallbackTimer);
                    // If we never received a payload, switch to polling
                    if (matches.length === 0 && !pollTimer.current) startPolling();
                    // Schedule reconnect after 30s
                    reconnectTimer.current = setTimeout(() => {
                        if (isMounted && (!wsRef.current || wsRef.current.readyState === WebSocket.CLOSED)) {
                            connectWs();
                        }
                    }, 30_000);
                };
            } catch (_) {
                startPolling();
            }
        };

        connectWs();

        return () => {
            isMounted = false;
            try { wsRef.current?.close(); } catch (_) {}
            if (pollTimer.current) clearInterval(pollTimer.current);
            if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [win, sport]);

    return { matches, sources, loading, source, connected, lastUpdate };
}
